// Petites Annonces - Design Premium avec Navigation Unifiée
// Style Leboncoin optimisé AbracadaBati - Catégories Bâtiment complètes

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MapPin, Heart, Plus, Clock, Camera, X, ArrowLeft,
  Package, Wrench, Hammer, Box, Eye, MessageCircle, Building2, SlidersHorizontal,
  Home, Menu, ChevronDown, User, Layers, ChevronRight, Settings, HelpCircle,
  Bell, LogOut, ClipboardList, RotateCcw, Store, Users, Briefcase, Newspaper,
  Zap, Droplets, Flower2, Waves, Paintbrush, Construction, RefreshCw, Grid3X3,
  TreePine, Sun, Flame, Filter, Tag, Euro, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import UniversalBanner from '../../components/layout/UniversalBanner';
import BetaBanner from '../../components/ui/BetaBanner';
import { MOCK_ANNONCES } from './AnnonceDetailPage';
import { CATEGORIES_BTP } from '../../data/categories-btp';

import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// CATÉGORIES BÂTIMENT - Import centralisé avec couleurs charte Abracadabâti
// ══════════════════════════════════════════════════════════════════════════════

// Mapper les catégories avec icônes Lucide et couleurs charte
const ICON_MAP = {
  'electricite': Zap,
  'plomberie': Droplets,
  'jardin': Flower2,
  'piscine': Waves,
  'peinture': Paintbrush,
  'maconnerie': Construction,
  'renovation': RefreshCw,
  'carrelage': Grid3X3,
  'menuiserie': TreePine,
  'chauffage': Flame,
  'couverture': Building2,
  'isolation': Layers,
  'climatisation': Layers,
  'facade': Building2,
  'solaire': Sun,
  'outillage': Wrench,
};

const CATEGORIES = CATEGORIES_BTP.map(cat => ({
  id: cat.id,
  label: cat.label,
  icon: ICON_MAP[cat.id] || Package,
  color: cat.color,
  bgColor: cat.color === '#F97316' ? '#FFF8F4' : '#F0F4F8',
  subcategories: cat.subcategories,
}));

// Marques populaires dans le bâtiment
const POPULAR_BRANDS = [
  'Bosch', 'Makita', 'DeWalt', 'Milwaukee', 'Hilti', 'Metabo',
  'Stanley', 'Facom', 'Grohe', 'Legrand', 'Schneider', 'Hager',
  'Weber', 'Sika', 'Knauf', 'Placo', 'Velux', 'Atlantic',
  'De Dietrich', 'Viessmann', 'Daikin', 'Karcher', 'Stihl', 'Husqvarna'
];

// Fourchettes de prix
const PRICE_RANGES = [
  { id: '0-50', label: 'Moins de 50€', min: 0, max: 50 },
  { id: '50-100', label: '50€ - 100€', min: 50, max: 100 },
  { id: '100-250', label: '100€ - 250€', min: 100, max: 250 },
  { id: '250-500', label: '250€ - 500€', min: 250, max: 500 },
  { id: '500-1000', label: '500€ - 1000€', min: 500, max: 1000 },
  { id: '1000+', label: 'Plus de 1000€', min: 1000, max: null },
];

// Liens de navigation vers les univers - Charte Abracadabâti
const UNIVERS_NAV = [
  { id: 'artisans', label: 'Artisans', path: '/artisans', icon: Wrench, color: '#1E3A5F' },
  { id: 'marketplace', label: 'Marketplace', path: '/produits', icon: Store, color: '#1E3A5F' },
  { id: 'annonces', label: 'Entre Particuliers', path: '/annonces', icon: Newspaper, color: '#F97316' },
  { id: 'reseau', label: 'Réseau Social', path: '/reseau', icon: Users, color: '#1E3A5F' },
  { id: 'emploi', label: 'Emploi', path: '/reseau/emploi', icon: Briefcase, color: '#1E3A5F' },
];

// Dropdown Catégories
function CategoriesDropdown({ isOpen, onClose, onSelect, selectedCategory }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 z-50"
      style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}
    >
      <div className="p-3">
        <button
          onClick={() => { onSelect(''); onClose(); }}
          className={`w-full text-left px-4 py-3 rounded-xl text-[14px] font-semibold transition-all ${
            !selectedCategory 
              ? 'bg-[#F97316]/10 text-[#F97316]' 
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          Toutes les catégories
        </button>
        <div className="border-t border-gray-100 my-2" />
        <div className="space-y-1">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => { onSelect(cat.id); onClose(); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[14px] transition-all ${
                  selectedCategory === cat.id 
                    ? 'bg-[#F97316]/10 text-[#F97316]' 
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <div 
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: cat.bgColor }}
                >
                  <Icon size={18} style={{ color: cat.color }} />
                </div>
                <span className="font-medium">{cat.label}</span>
                <span className="ml-auto text-xs text-gray-400">({cat.count})</span>
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// Menu Hamburger
function AnnoncesMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  
  const menuSections = [
    {
      title: 'Mes annonces',
      items: [
        { icon: Plus, label: 'Déposer une annonce', path: '/annonces/deposer', color: '#F97316' },
        { icon: ClipboardList, label: 'Mes annonces', path: '/compte/annonces', color: '#0EA5E9' },
        { icon: Heart, label: 'Mes favoris', path: '/annonces/favoris', color: '#EC4899' },
        { icon: Package, label: 'Mes commandes', path: '/annonces/commandes', color: '#8B5CF6' },
        { icon: MessageCircle, label: 'Mes messages', path: '/compte/messages', color: '#10B981' },
      ]
    },
    {
      title: 'Mon compte',
      items: [
        { icon: User, label: 'Mon profil', path: '/compte', color: '#10B981' },
        { icon: Settings, label: 'Paramètres', path: '/compte/parametres', color: '#6B7280' },
        { icon: Bell, label: 'Notifications', path: '/compte/notifications', color: '#F97316' },
      ]
    },
    {
      title: 'Aide',
      items: [
        { icon: HelpCircle, label: 'Comment ça marche', path: '/aide/annonces', color: '#3B82F6' },
        { icon: HelpCircle, label: 'Service client', path: '/aide', color: '#6366F1' },
      ]
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D4A6F] px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <User size={22} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold">Bonjour</p>
                  <Link to="/reseau/connexion" className="text-white/80 text-sm hover:underline">
                    Se connecter / S'inscrire
                  </Link>
                </div>
                <button onClick={onClose} className="p-2">
                  <X size={22} className="text-white" />
                </button>
              </div>
            </div>
            
            {/* Menu Sections */}
            <div className="flex-1 overflow-y-auto">
              {menuSections.map((section, sIdx) => (
                <div key={sIdx} className="border-b border-gray-200">
                  <h3 className="px-4 py-3 text-sm font-bold text-gray-900 bg-gray-50">
                    {section.title}
                  </h3>
                  <div className="py-2">
                    {section.items.map((item, iIdx) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={iIdx}
                          onClick={() => { navigate(item.path); onClose(); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${item.color}15` }}
                          >
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{item.label}</span>
                          <ChevronRight size={16} className="ml-auto text-gray-400" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-200 p-4">
              <button className="w-full flex items-center justify-center gap-2 py-2 text-gray-600 hover:text-gray-900">
                <LogOut size={18} />
                <span className="text-sm">Déconnexion</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Annonce Card Component
function AnnonceCard({ annonce, onFavorite, isFavorite, index, isTest = false }) {
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return "À l'instant";
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-2xl overflow-hidden cursor-pointer group relative"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
      onClick={() => navigate(`/annonces/${annonce.id || annonce._id}`)}
      whileHover={{ y: -2, boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }}
      data-testid="annonce-card"
    >
      <div className="flex">
        {/* Image */}
        <div className="relative w-36 sm:w-44 flex-shrink-0">
          <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100">
            {annonce.photos && annonce.photos.length > 0 ? (
              <img
                src={annonce.photos[0]}
                alt={annonce.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Camera size={32} className="text-gray-300" />
              </div>
            )}
          </div>

          {/* Badge TEST pour annonces de préouverture */}
          {isTest && (
            <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1 shadow-md">
              <Info size={10} />
              TEST
            </div>
          )}

          {annonce.photos && annonce.photos.length > 1 && (
            <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1">
              <Camera size={12} />
              {annonce.photos.length}
            </div>
          )}

          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onFavorite?.(annonce.id || annonce._id);
            }}
            className={`absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              isFavorite
                ? 'bg-red-500 text-white'
                : 'bg-white/90 backdrop-blur-sm text-gray-400 hover:text-red-500'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
          </motion.button>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 line-clamp-2 text-[15px] mb-1">
              {annonce.title}
            </h3>
            
            <p className="text-[#F97316] font-bold text-xl mb-2">
              {formatPrice(annonce.price)}
            </p>

            <div className="flex flex-wrap gap-2 mb-3">
              {annonce.condition && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                  {annonce.condition}
                </span>
              )}
              {annonce.price_negotiable && (
                <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded text-xs">
                  Négociable
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <MapPin size={12} />
              <span>{annonce.city || annonce.location}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={12} />
              <span>{formatTimeAgo(annonce.created_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Main Page
export default function PetitesAnnoncesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  // UI States
  const [showCategories, setShowCategories] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Search state from URL
  const searchQuery = searchParams.get('q') || '';
  const searchCategory = searchParams.get('cat') || '';
  const searchCity = searchParams.get('city') || '';

  // Charger les favoris depuis le localStorage au montage
  useEffect(() => {
    const storedFavorites = JSON.parse(localStorage.getItem('annonces_favoris') || '[]');
    setFavorites(new Set(storedFavorites));
  }, []);

  useEffect(() => {
    fetchAnnonces();
  }, [searchParams]);

  const fetchAnnonces = async () => {
    setLoading(true);
    try {
      // Utiliser les annonces de test MOCK_ANNONCES
      let filteredAnnonces = [...MOCK_ANNONCES];
      
      // Appliquer les filtres
      const q = searchParams.get('q');
      const cat = searchParams.get('cat');
      const subcat = searchParams.get('subcat');
      const price = searchParams.get('price');
      const brand = searchParams.get('brand');
      
      if (q) {
        const searchLower = q.toLowerCase();
        filteredAnnonces = filteredAnnonces.filter(a => 
          a.title.toLowerCase().includes(searchLower) ||
          a.description.toLowerCase().includes(searchLower)
        );
      }
      
      if (cat) {
        filteredAnnonces = filteredAnnonces.filter(a => a.category === cat);
      }
      
      if (subcat) {
        filteredAnnonces = filteredAnnonces.filter(a => a.subcategory === subcat);
      }
      
      if (brand) {
        filteredAnnonces = filteredAnnonces.filter(a => a.brand === brand);
      }
      
      if (price) {
        const priceRange = PRICE_RANGES.find(p => p.id === price);
        if (priceRange) {
          filteredAnnonces = filteredAnnonces.filter(a => {
            if (priceRange.max === null) return a.price >= priceRange.min;
            return a.price >= priceRange.min && a.price <= priceRange.max;
          });
        }
      }
      
      setAnnonces(filteredAnnonces);
    } catch (e) {
      console.error('Error fetching annonces:', e);
      setAnnonces(MOCK_ANNONCES);
    } finally {
      setLoading(false);
    }
  };

  // Handle search from UnifiedHeader
  const handleHeaderSearch = ({ query, city, category }) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (city) params.set('city', city);
    if (category) params.set('cat', mapCategoryLabel(category));
    setSearchParams(params);
  };

  // Map category label to id
  const mapCategoryLabel = (label) => {
    const mapping = {
      'Matériaux': 'materiaux',
      'Outillage': 'outils',
      'Véhicules': 'equipements',
      'Services': 'surplus_chantier'
    };
    return mapping[label] || '';
  };

  const handleCategorySelect = (catId) => {
    const params = new URLSearchParams(searchParams);
    if (catId) {
      params.set('cat', catId);
    } else {
      params.delete('cat');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const handleFavorite = async (annonceId) => {
    const storedFavorites = JSON.parse(localStorage.getItem('annonces_favoris') || '[]');
    const newFavorites = new Set(favorites);
    
    if (newFavorites.has(annonceId)) {
      newFavorites.delete(annonceId);
      const updatedStored = storedFavorites.filter(id => id !== annonceId);
      localStorage.setItem('annonces_favoris', JSON.stringify(updatedStored));
      toast.success('Retiré des favoris');
    } else {
      newFavorites.add(annonceId);
      storedFavorites.push(annonceId);
      localStorage.setItem('annonces_favoris', JSON.stringify(storedFavorites));
      toast.success('Ajouté aux favoris');
    }
    setFavorites(newFavorites);
  };

  const selectedCategoryLabel = searchCategory ? CATEGORIES.find(c => c.id === searchCategory)?.label : 'Toutes catégories';
  const selectedCategory = CATEGORIES.find(c => c.id === searchCategory);
  
  // États pour les filtres avancés
  const [showFilters, setShowFilters] = useState(false);
  const [showDropdown, setShowDropdown] = useState(null); // 'price' ou 'brand'
  const [filterSection, setFilterSection] = useState(null); // 'price' ou 'brand' pour scroller vers la section
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcat') || '');
  const [selectedPriceRange, setSelectedPriceRange] = useState(searchParams.get('price') || '');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '');

  // Ouvrir le panneau et scroller vers une section spécifique
  const openFiltersWithSection = (section) => {
    setFilterSection(section);
    setShowFilters(true);
    // Scroller vers la section après un court délai pour que le panneau soit rendu
    setTimeout(() => {
      const element = document.getElementById(`filter-section-${section}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  // Gérer les sous-catégories
  const handleSubcategorySelect = (subcatId) => {
    const params = new URLSearchParams(searchParams);
    if (subcatId) {
      params.set('subcat', subcatId);
    } else {
      params.delete('subcat');
    }
    setSelectedSubcategory(subcatId);
    setSearchParams(params);
  };

  // Gérer les filtres de prix
  const handlePriceSelect = (priceId) => {
    const params = new URLSearchParams(searchParams);
    if (priceId) {
      params.set('price', priceId);
    } else {
      params.delete('price');
    }
    setSelectedPriceRange(priceId);
    setSearchParams(params);
  };

  // Gérer les filtres de marque
  const handleBrandSelect = (brand) => {
    const params = new URLSearchParams(searchParams);
    if (brand) {
      params.set('brand', brand);
    } else {
      params.delete('brand');
    }
    setSelectedBrand(brand);
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif]" data-testid="petites-annonces-page">
      {/* Header Unifié - Design System */}
      <UnifiedHeader onSearch={handleHeaderSearch} />
      
      {/* Navigation Principale - Design System */}
      <MainNavigation />

      {/* Spacer Mobile - 44px pour le menu horizontal fixed */}
      <div className="h-11 md:hidden" />

      {/* Bandeau de Conversion Universel */}
      <UniversalBanner variant="particuliers" className="py-4" />

      {/* ══════════════════════════════════════════════════════════════════
          BARRE DE FILTRES UNIFIÉE - Style pills orange
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '12px 16px',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        background: '#FFFFFF',
        borderBottom: '1px solid #F3F4F6',
        WebkitOverflowScrolling: 'touch',
        position: 'sticky',
        top: '92px',
        zIndex: 30,
      }} className="hide-scrollbar sm:top-[104px]">
        {/* Bouton Catégories & Filtres */}
        <button
          onClick={() => { setShowFilters(true); setShowDropdown(null); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: searchCategory ? '#FFF8F4' : '#FFFFFF',
            border: searchCategory ? '1.5px solid #E8650A' : '1.5px solid #E5E7EB',
            borderRadius: '20px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '13px',
            fontWeight: searchCategory ? '600' : '500',
            color: searchCategory ? '#E8650A' : '#1a2a4a',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
          data-testid="open-filters-btn"
        >
          <SlidersHorizontal size={14} color={searchCategory ? '#E8650A' : '#1a2a4a'} />
          {searchCategory ? selectedCategoryLabel : 'Catégories & Filtres'}
          {searchCategory && (
            <span style={{
              background: '#E8650A',
              color: '#FFFFFF',
              borderRadius: '10px',
              padding: '1px 6px',
              fontSize: '10px',
              fontWeight: '700',
            }}>1</span>
          )}
        </button>

        {/* Bouton Prix */}
        <button
          onClick={() => openFiltersWithSection('price')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: selectedPriceRange ? '#FFF8F4' : '#FFFFFF',
            border: selectedPriceRange ? '1.5px solid #E8650A' : '1.5px solid #E5E7EB',
            borderRadius: '20px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '13px',
            fontWeight: selectedPriceRange ? '600' : '500',
            color: selectedPriceRange ? '#E8650A' : '#1a2a4a',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <Euro size={14} color={selectedPriceRange ? '#E8650A' : '#1a2a4a'} />
          {selectedPriceRange ? PRICE_RANGES.find(p => p.id === selectedPriceRange)?.label : 'Prix'}
        </button>

        {/* Bouton Ville */}
        <button
          onClick={() => openFiltersWithSection('location')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: '#FFFFFF',
            border: '1.5px solid #E5E7EB',
            borderRadius: '20px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '13px',
            fontWeight: '500',
            color: '#1a2a4a',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <MapPin size={14} color="#1a2a4a" />
          Ville
        </button>
        
        <style>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          PANNEAU LATÉRAL "TOUS LES FILTRES" - Slide-in depuis la gauche
      ══════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showFilters && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFilters(false)}
              className="fixed inset-0 bg-black/40 z-50"
            />
            
            {/* Panneau latéral gauche */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-[320px] max-w-[85vw] bg-white z-50 shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Header du panneau */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Tous les filtres</h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              
              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                
                {/* Section Catégories */}
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Layers size={16} className="text-[#1E40AF]" />
                    Catégorie
                  </h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => { handleCategorySelect(''); setSelectedSubcategory(''); }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                        !searchCategory 
                          ? 'bg-[#1E40AF] text-white font-medium' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Toutes les catégories
                    </button>
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      const isActive = searchCategory === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => { handleCategorySelect(cat.id); setSelectedSubcategory(''); }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                            isActive 
                              ? 'text-white font-medium' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                          style={isActive ? { backgroundColor: cat.color } : {}}
                        >
                          <Icon size={16} />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Section Sous-catégories (si catégorie sélectionnée) */}
                {selectedCategory && selectedCategory.subcategories && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight size={16} className="text-[#F97316]" />
                      Sous-catégorie
                    </h3>
                    <div className="space-y-1">
                      <button
                        onClick={() => handleSubcategorySelect('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          !selectedSubcategory 
                            ? 'bg-[#F97316]/10 text-[#F97316] font-medium' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Tout {selectedCategory.label}
                      </button>
                      {selectedCategory.subcategories.map((subcat) => (
                        <button
                          key={subcat.id}
                          onClick={() => handleSubcategorySelect(subcat.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                            selectedSubcategory === subcat.id 
                              ? 'bg-[#F97316]/10 text-[#F97316] font-medium' 
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {subcat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Section Prix */}
                <div id="filter-section-price">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Euro size={16} className="text-emerald-600" />
                    Prix
                  </h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => handlePriceSelect('')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        !selectedPriceRange 
                          ? 'bg-emerald-50 text-emerald-700 font-medium' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Tous les prix
                    </button>
                    {PRICE_RANGES.map((range) => (
                      <button
                        key={range.id}
                        onClick={() => handlePriceSelect(range.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          selectedPriceRange === range.id 
                            ? 'bg-emerald-50 text-emerald-700 font-medium' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Section Marques */}
                <div id="filter-section-brand">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Tag size={16} className="text-blue-600" />
                    Marque
                  </h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    <button
                      onClick={() => handleBrandSelect('')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        !selectedBrand 
                          ? 'bg-blue-50 text-blue-700 font-medium' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Toutes les marques
                    </button>
                    {POPULAR_BRANDS.map((brand) => (
                      <button
                        key={brand}
                        onClick={() => handleBrandSelect(brand)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          selectedBrand === brand 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {brand}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Footer du panneau */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-2">
                <Button
                  onClick={() => setShowFilters(false)}
                  className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-xl font-semibold"
                >
                  Voir les résultats ({annonces.length})
                </Button>
                <button
                  onClick={() => {
                    clearFilters();
                    setSelectedSubcategory('');
                    setSelectedPriceRange('');
                    setSelectedBrand('');
                  }}
                  className="w-full py-2 text-sm text-red-600 hover:underline font-medium"
                >
                  Réinitialiser tous les filtres
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Filtres actifs + Compteur */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between">
            {/* Compteur résultats */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{annonces.length} annonces</span>
              {(searchCategory || selectedSubcategory || selectedPriceRange || selectedBrand) && (
                <span className="text-xs text-gray-500">avec filtres</span>
              )}
            </div>
            
            {/* Tags filtres actifs */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              {searchCategory && (
                <span className="inline-flex items-center gap-1 bg-[#1E40AF]/10 text-[#1E40AF] px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                  {selectedCategoryLabel}
                  <button onClick={() => { handleCategorySelect(''); setSelectedSubcategory(''); }} className="hover:bg-[#1E40AF]/20 rounded-full">
                    <X size={12} />
                  </button>
                </span>
              )}
              {selectedSubcategory && selectedCategory && (
                <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                  {selectedCategory.subcategories.find(s => s.id === selectedSubcategory)?.label}
                  <button onClick={() => handleSubcategorySelect('')} className="hover:bg-purple-200 rounded-full">
                    <X size={12} />
                  </button>
                </span>
              )}
              {selectedPriceRange && (
                <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                  {PRICE_RANGES.find(p => p.id === selectedPriceRange)?.label}
                  <button onClick={() => handlePriceSelect('')} className="hover:bg-emerald-200 rounded-full">
                    <X size={12} />
                  </button>
                </span>
              )}
              {selectedBrand && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                  {selectedBrand}
                  <button onClick={() => handleBrandSelect('')} className="hover:bg-blue-200 rounded-full">
                    <X size={12} />
                  </button>
                </span>
              )}
              {(searchCategory || selectedSubcategory || selectedPriceRange || selectedBrand) && (
                <button 
                  onClick={() => {
                    clearFilters();
                    setSelectedSubcategory('');
                    setSelectedPriceRange('');
                    setSelectedBrand('');
                  }} 
                  className="text-xs text-red-600 hover:underline font-medium whitespace-nowrap"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Beta Banner */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <BetaBanner />
      </div>

      {/* Annonces List */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : annonces.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Package size={56} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-xl text-gray-900 mb-2">Aucune annonce trouvée</h3>
            <p className="text-gray-500 mb-6">Soyez le premier à déposer une annonce !</p>
            <Link to="/annonces/deposer">
              <Button className="bg-[#F97316] hover:bg-[#EA580C] rounded-xl font-semibold">
                <Plus size={18} className="mr-2" />
                Déposer une annonce
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
            {annonces.map((annonce, index) => (
              <AnnonceCard
                key={annonce.id || annonce._id}
                annonce={annonce}
                index={index}
                onFavorite={handleFavorite}
                isFavorite={favorites.has(annonce.id || annonce._id)}
                isTest={annonce.id?.startsWith('test-')}
              />
            ))}
          </div>
        )}
      </main>

      {/* Menu */}
      <AnnoncesMenu
        isOpen={showMenu}
        onClose={() => setShowMenu(false)}
      />
    </div>
  );
}
